import type { DpiVerdict, SpotLimits } from "../../src/image/dpi.ts";
import type { SpotGeometry } from "../../src/image/placement.ts";
import { EMAIL_RE, staffDomainAllowed, staffDomainMessage } from "../../src/lib/email-address.ts";
import type { Spot } from "../../src/lib/types.ts";
import { SPOTS } from "../../src/lib/types.ts";
import { defaultWidth, printSize, sliderBounds } from "./cart.ts";

export interface StudioGarment {
  id: string;
  name: string;
  style: string | null;
  color: string;
  tone: "light" | "dark";
  ppi: number;
  spots: Partial<Record<Spot, SpotGeometry>>;
  photoUrl: string;
  backPhotoUrl: string | null;
  // The 160 px swatch (the front photo when the garment has none yet).
  thumbUrl: string;
}

export interface Staff {
  name: string;
  email: string;
}

// "original" keeps the logo's colors; black and white repaint every opaque pixel in that one color.
export type Ink = "original" | "black" | "white";
export const INKS: Ink[] = ["original", "black", "white"];
export const INK_LABEL: Record<Ink, string> = { original: "Original", black: "Black", white: "White" };

export const SPOT_LABEL: Record<Spot, string> = { "front-center": "Front center", "left-chest": "Left chest", "full-back": "Full back" };

// The server's address check (src/lib/email-address.ts): a plain address only.
const EMAIL = EMAIL_RE;
const STAFF_KEY = "pressroom.studio.staff";

// The tightest limits for a spot across the colors picked (the server sizes the sheet the same way).
export function spotLimits(garments: StudioGarment[], spot: Spot): SpotLimits | null {
  if (garments.length === 0) return null;
  let maxWidthIn = Number.POSITIVE_INFINITY;
  let maxHeightIn = Number.POSITIVE_INFINITY;
  for (const g of garments) {
    const s = g.spots[spot];
    if (!s) return null;
    maxWidthIn = Math.min(maxWidthIn, s.maxWidthIn);
    maxHeightIn = Math.min(maxHeightIn, s.maxHeightIn);
  }
  return { maxWidthIn, maxHeightIn };
}

// domains comes from the catalog (limits.staffDomains); without it only the address's form is checked.
export function staffErrors(f: Staff, domains?: string[]): Partial<Record<"name" | "email", string>> {
  const e: Partial<Record<"name" | "email", string>> = {};
  const email = f.email.trim();
  if (!f.name.trim() || f.name.trim().length > 100) e.name = "Enter your name.";
  if (!EMAIL.test(email) || email.length > 320) e.email = "Enter a valid email address.";
  else if (domains && !staffDomainAllowed(email, domains)) e.email = staffDomainMessage(domains);
  return e;
}

export function sendErrors(f: { name: string; email: string; note: string }): Partial<Record<"name" | "email" | "note", string>> {
  const e: Partial<Record<"name" | "email" | "note", string>> = {};
  if (!f.name.trim()) e.name = "Enter the customer's name.";
  if (!EMAIL.test(f.email.trim())) e.email = "Enter a valid email address.";
  if (f.note.trim().length > 500) e.note = "Keep the note to 500 characters or fewer.";
  return e;
}

// localStorage can be missing or throw (private windows, blocked storage); the page then just asks again.
export function loadStaff(storage?: Pick<Storage, "getItem">): Staff | null {
  try {
    const v = JSON.parse(storage?.getItem(STAFF_KEY) ?? "null") as unknown;
    if (!v || typeof v !== "object") return null;
    const { name, email } = v as Record<string, unknown>;
    if (typeof name !== "string" || typeof email !== "string") return null;
    const staff = { name: name.trim(), email: email.trim() };
    return Object.keys(staffErrors(staff)).length === 0 ? staff : null;
  } catch {
    return null;
  }
}

export function saveStaff(storage: Pick<Storage, "setItem"> | undefined, staff: Staff): void {
  try {
    storage?.setItem(STAFF_KEY, JSON.stringify(staff));
  } catch {
    // Not remembered; the page asks again next time.
  }
}

// Nothing prints from a mockup, so low resolution is a warning here, never a block.
export function studioDpiNotice(dpi: number, verdict: DpiVerdict): { cls: "good" | "warn"; text: string } {
  if (verdict === "ok") return { cls: "good", text: `Sharp at this size (${dpi} DPI).` };
  if (verdict === "warn") return { cls: "warn", text: `A little soft at this size (${dpi} DPI). Fine for a mockup.` };
  return { cls: "warn", text: `Soft at this size (${dpi} DPI). Fine for a mockup; ask the customer for a larger file before it prints.` };
}

// Mean Rec. 601 luminance of the opaque pixels (alpha >= 128, the threshold the garment tone check uses),
// from canvas RGBA data. Null when nothing is opaque.
export function logoTone(rgba: Uint8ClampedArray | Uint8Array): "light" | "dark" | null {
  let sum = 0;
  let n = 0;
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    if (rgba[i + 3]! < 128) continue;
    sum += 0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!;
    n++;
  }
  if (n === 0) return null;
  return sum / n < 128 ? "dark" : "light";
}

// The share of pixels with alpha >= 128, from 0 to 1 (0 for no pixels).
export function opaqueShare(rgba: Uint8ClampedArray | Uint8Array): number {
  let n = 0;
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i]! >= 128) n++;
  return rgba.length < 4 ? 0 : n / Math.floor(rgba.length / 4);
}

// One-color ink repaints every opaque pixel, so a logo with no transparent area would print as a solid rectangle.
// Unknown (not measured yet) counts as fine; the page re-checks when the measurement lands.
// Same rule as hasTransparency in src/image/sheet.ts (opaque share under 0.99, alpha 128 line): keep the two identical.
export function inkAllowed(share: number | null): boolean {
  return share === null || share < 0.99;
}

const inkTone = (tone: "light" | "dark" | null, ink: Ink) => (ink === "black" ? "dark" : ink === "white" ? "light" : tone);

// The colors the logo would vanish on: light art on a light garment, dark on dark. One-color ink counts as its own tone.
export function toneClashes(tone: "light" | "dark" | null, ink: Ink, garments: StudioGarment[]): StudioGarment[] {
  const t = inkTone(tone, ink);
  return t ? garments.filter((g) => g.tone === t) : [];
}

export const listWords = (words: string[]) => (words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`);
// Hand-registered garments have no style chip to say what they are, so they carry their name.
const colorName = (g: StudioGarment) => (g.style ? g.color : `${g.name}, ${g.color}`);

// The same-tone warning, or null. Light-tone tiles use a multiply blend, so white ink there is invisible. With both
// tones picked every one-color ink clashes with something, so the advice is Original (when it clashes with none of
// them) or a separate sheet, never the other ink. inkOk false (no transparent area) leaves ink out of the advice.
// split is the advice when the colors picked need different inks: the Studio says to make a sheet of their own, the
// designer to send them as a separate design.
export function inkNotice(
  tone: "light" | "dark" | null,
  ink: Ink,
  garments: StudioGarment[],
  inkOk = true,
  split = "Put those colors on a sheet of their own.",
): string | null {
  const clashes = toneClashes(tone, ink, garments);
  if (clashes.length === 0) return null;
  const t = inkTone(tone, ink)!;
  const colors = listWords([...new Set(clashes.map(colorName))]);
  const lead = ink === "original" ? `This logo is ${t}, so it may disappear on ${colors}.` : `${INK_LABEL[ink]} ink may disappear on ${colors}.`;
  if (ink !== "original" && toneClashes(tone, "original", garments).length === 0) return `${lead} Try Ink: Original.`;
  if (garments.some((g) => g.tone !== t)) return `${lead} ${split}`;
  if (!inkOk) return lead;
  return `${lead} Try Ink: ${t === "light" ? "Black" : "White"}.`;
}

type LogoSize = { widthPx: number; heightPx: number } | null;

// The slider's range for a spot across the colors picked, or null when it cannot be placed (no logo, or a color
// without the spot).
export function spotBounds(garments: StudioGarment[], logo: LogoSize, spot: Spot, printerMaxWidthIn: number): { min: number; max: number } | null {
  const limits = spotLimits(garments, spot);
  if (!limits || !logo) return null;
  return sliderBounds(limits, logo.heightPx / logo.widthPx, printerMaxWidthIn);
}

// The print size at the slider's width (the server sizes the sheet the same way), or null when it cannot be placed.
export function sizeAt(garments: StudioGarment[], logo: LogoSize, spot: Spot, widthIn: number, printerMaxWidthIn: number) {
  const limits = spotLimits(garments, spot);
  if (!limits || !logo || !(widthIn > 0)) return null;
  return printSize(limits, logo.heightPx / logo.widthPx, widthIn, printerMaxWidthIn);
}

// Slider widths after the colors or the logo change: a new logo (reset) gets fresh defaults; otherwise each width is
// kept and clamped to the new range. 0 where the spot cannot be placed.
export function fittedWidths(
  garments: StudioGarment[],
  logo: LogoSize,
  widths: Record<Spot, number>,
  printerMaxWidthIn: number,
  reset = false,
): Record<Spot, number> {
  const out = { ...widths };
  for (const spot of SPOTS) {
    const b = spotBounds(garments, logo, spot, printerMaxWidthIn);
    if (!b || !logo) out[spot] = 0;
    else if (reset || !(widths[spot] > 0)) out[spot] = defaultWidth(b, logo.widthPx);
    else out[spot] = Math.min(b.max, Math.max(b.min, widths[spot]));
  }
  return out;
}

// Placements every picked color can take: one they cannot is switched off, and the first usable spot comes on when
// nothing is left on. With no colors picked the switches stay as they are.
export function usableSpots(on: Record<Spot, boolean>, garments: StudioGarment[]): Record<Spot, boolean> {
  if (garments.length === 0) return on;
  const out = { ...on };
  for (const spot of SPOTS) if (out[spot] && !spotLimits(garments, spot)) out[spot] = false;
  if (!SPOTS.some((sp) => out[sp])) {
    const first = SPOTS.find((sp) => spotLimits(garments, sp));
    if (first) out[first] = true;
  }
  return out;
}

// ---- staff order entry with a pay link (spec 2026-10-07 section 2) ----

export interface PayCustomer {
  name: string;
  email: string;
  phone: string;
  note: string;
}

// The pay link form's checks, before anything is sent (the server checks the same).
export function payLinkErrors(c: PayCustomer, quantities: Record<string, Record<string, number>>): Partial<Record<keyof PayCustomer | "quantities", string>> {
  const e: Partial<Record<keyof PayCustomer | "quantities", string>> = {};
  if (!c.name.trim()) e.name = "Enter the customer's name.";
  if (!EMAIL.test(c.email.trim())) e.email = "Enter a valid email address.";
  const phone = c.phone.trim();
  if (phone && (phone.length > 40 || (phone.match(/\d/g) ?? []).length < 7)) e.phone = "Enter the customer's phone with digits, or leave it blank.";
  if (c.note.trim().length > 500) e.note = "Keep the note to 500 characters or fewer.";
  if (garmentTotal(quantities) === 0) e.quantities = "Enter at least one garment for the customer.";
  return e;
}

export function garmentTotal(quantities: Record<string, Record<string, number>>): number {
  return Object.values(quantities).reduce((n, q) => n + Object.values(q).reduce((a, b) => a + b, 0), 0);
}

// An open pay link's line in the list: who, how many, the two totals, and until when (or since when expired).
export function payLinkLine(l: { customerName: string; customerEmail: string; garmentCount: number; staffName: string; createdOn: string; expiresOn: string; expired: boolean }): string {
  const n = `${l.garmentCount} ${l.garmentCount === 1 ? "garment" : "garments"}`;
  return `${l.customerName} (${l.customerEmail}), ${n}. Sent by ${l.staffName} on ${l.createdOn}. ${l.expired ? `Expired ${l.expiresOn}.` : `Works until ${l.expiresOn}.`}`;
}
